<template>
  <div class="user-agreements">
    <AppSectionHero
      :title="$t('pages.user.agreements.title')"
      :subtitle="$t('pages.user.agreements.subtitle')"
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectStatusCard"
    />

    <CCard class="border-0 shadow-sm create-shell">
      <CCardBody class="p-4 create-shell__body">
        <div>
          <div class="create-shell__eyebrow">{{ text.startHere }}</div>
          <div class="create-shell__title">{{ text.createTitle }}</div>
          <div class="create-shell__copy">{{ text.createCopy }}</div>
        </div>
        <CButton color="primary" size="lg" class="create-shell__button" to="/dashboard/user-agreements/new">
          <CIcon name="cil-plus" class="mr-2" />
          {{ text.createButton }}
        </CButton>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm draft-shell">
      <CCardBody class="p-4">
        <div class="draft-shell__header">
          <div>
            <div class="draft-shell__eyebrow">{{ text.draftSamples }}</div>
            <div class="draft-shell__title">{{ text.draftSamplesCopy }}</div>
          </div>
        </div>
        <div class="draft-grid">
          <div
            v-for="draft in localizedDraftTemplates"
            :key="draft.name"
            class="draft-card"
          >
            <div class="draft-card__type">{{ draft.type }}</div>
            <div class="draft-card__title">{{ draft.name }}</div>
            <div class="draft-card__copy">{{ draft.description }}</div>
            <div class="draft-card__actions">
              <button class="secondary-btn draft-card__btn" @click="previewDraftTemplate(draft)">
                <CIcon name="cil-description" />
                {{ text.previewDraft }}
              </button>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="previewDraft" class="draft-preview-modal" @click.self="closeDraftPreview">
      <div class="draft-preview-modal__card">
        <div class="draft-preview-modal__header">
          <div>
            <div class="draft-preview-modal__eyebrow">{{ previewDraft.type }}</div>
            <div class="draft-preview-modal__title">{{ previewDraft.name }}</div>
          </div>
          <button class="draft-preview-modal__close" @click="closeDraftPreview">✕</button>
        </div>
        <div class="draft-preview-modal__body">
          <iframe
            v-if="previewDraft.preview"
            :src="previewDraft.preview"
            class="draft-preview-modal__frame"
            :title="text.previewDraftFrameTitle"
          />
        </div>
      </div>
    </div>

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-4">
        <div class="toolbar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input v-model="searchQuery" :placeholder="text.searchPlaceholder">
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard v-if="loadError" class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="error-state">{{ loadError }}</div>
      </CCardBody>
    </CCard>
    <CCard v-else-if="isLoading" class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="empty-state">{{ text.loading }}</div>
      </CCardBody>
    </CCard>
    <CCard v-else-if="filteredAgreements.length" class="border-0 shadow-sm table-shell">
      <CCardBody class="p-4">
        <div class="table-head">
          <div class="table-head__copy">{{ text.showing(filteredAgreements.length) }}</div>
        </div>

        <div class="agreement-table">
          <div class="agreement-table__header">
            <div>{{ text.columns.agreement }}</div>
            <div>{{ text.columns.type }}</div>
            <div>{{ text.columns.partner }}</div>
            <div>{{ text.columns.status }}</div>
            <div>{{ text.columns.actions }}</div>
          </div>

          <div
            v-for="item in filteredAgreements"
            :key="item.id"
            class="agreement-table__row"
          >
            <div class="agreement-table__cell">
              <div class="agreement-title">{{ item.title }}</div>
              <div class="agreement-meta">{{ item.country }} · {{ item.activityType }}</div>
            </div>
            <div class="agreement-table__cell">
              <span class="agreement-value">{{ item.activityType }}</span>
            </div>
            <div class="agreement-table__cell">
              <span class="agreement-value">{{ item.partner }}</span>
            </div>
            <div class="agreement-table__cell">
              <span class="status-pill" :class="statusPillClass(item)">{{ displayStageLabel(item) }}</span>
            </div>
            <div class="agreement-table__cell agreement-table__cell--actions">
              <button
                class="secondary-btn table-action-btn"
                :title="text.openItem(item.title)"
                :aria-label="text.openItem(item.title)"
                @click="openAgreement(item)"
              >
                <CIcon name="cil-folder-open" />
              </button>
              <button
                v-if="item.derivedStage === 'draft'"
                class="danger-btn table-action-btn table-action-btn--danger"
                :title="text.deleteItem(item.title)"
                :aria-label="text.deleteItem(item.title)"
                @click="deleteDraft(item)"
              >
                <CIcon name="cil-trash" />
              </button>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>
    <CCard v-else class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="empty-state">{{ text.empty }}</div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Service from '@/service/api'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import {
  getAgreementWorkflowStatusLabel,
  getAgreementWorkflowStatusTone
} from '@/utils/agreement-workflow-status'
import { showConfirmAlert } from '@/utils/sweet-alert'

const USER_AGREEMENTS_CACHE_KEY = 'user-agreements-page'
const USER_AGREEMENTS_CACHE_MAX_AGE = 5 * 60 * 1000

const draftTemplates = [
  {
    name: 'Exchange Student 2023',
    type: 'Exchange Student',
    descriptions: {
      en: 'Reference layout for exchange student agreements.',
      th: 'ตัวอย่างรูปแบบเอกสารสำหรับข้อตกลงแลกเปลี่ยนนักศึกษา'
    },
    file: `${process.env.BASE_URL}drafts/Exchange%20Student%202023.docx`,
    preview: `${process.env.BASE_URL}draft-previews/exchange-student-2023.html`
  },
  {
    name: 'Double Degree PROGRAM',
    type: 'Double Degree',
    descriptions: {
      en: 'Reference layout for double degree program agreements.',
      th: 'ตัวอย่างรูปแบบเอกสารสำหรับข้อตกลงหลักสูตรปริญญาร่วม'
    },
    file: `${process.env.BASE_URL}drafts/Double%20Degree%20PROGRAM.docx`,
    preview: `${process.env.BASE_URL}draft-previews/double-degree-program.html`
  },
  {
    name: 'Extension Agreement MFU XXX',
    type: 'Extension Agreement',
    descriptions: {
      en: 'Reference layout for extension agreements and renewals.',
      th: 'ตัวอย่างรูปแบบเอกสารสำหรับข้อตกลงต่ออายุหรือขยายระยะเวลา'
    },
    file: `${process.env.BASE_URL}drafts/Extension%20Agreement_MFU_XXX.docx`,
    preview: `${process.env.BASE_URL}draft-previews/extension-agreement.html`
  },
  {
    name: 'MoU 2020',
    type: 'MOU File',
    descriptions: {
      en: 'Reference layout for memorandum of understanding documents.',
      th: 'ตัวอย่างรูปแบบเอกสารบันทึกความเข้าใจ'
    },
    file: `${process.env.BASE_URL}drafts/MoU%202020.doc`,
    preview: `${process.env.BASE_URL}draft-previews/mou-2020.html`
  }
]

const PAGE_TEXT = {
  en: {
    startHere: 'Start Here',
    createTitle: 'Need to draft a new agreement?',
    createCopy: 'Review the sample drafts below first so users can see the document style before creating a new agreement.',
    createButton: 'Create New Agreement',
    draftSamples: 'Draft Samples',
    draftSamplesCopy: 'Preview how each agreement draft looks before you start',
    previewDraft: 'Preview Draft',
    previewDraftFrameTitle: 'Draft preview',
    searchPlaceholder: 'Search by title, partner, country, or type',
    loading: 'Loading agreements...',
    empty: 'No agreements match your search.',
    stats: {
      drafts: 'Drafts',
      inReview: 'Review',
      approved: 'Approved'
    },
    stage: {
      draft: 'Draft',
      inReview: 'Review',
      approved: 'Approved'
    },
    columns: {
      agreement: 'Agreement',
      type: 'Type',
      partner: 'Partner',
      status: 'Status',
      actions: 'Actions'
    },
    showing: (count) => `Showing ${count} agreements`,
    openItem: (title) => `Open ${title || 'agreement'}`,
    deleteItem: (title) => `Delete ${title || 'draft agreement'}`,
    loadError: 'The agreements page could not be loaded right now.',
    confirmDeleteTitle: 'Delete Draft?',
    confirmDeleteText: (title) => `Delete draft "${title || 'Untitled agreement'}"?`,
    confirmDeleteButton: 'Delete',
    cancelDeleteButton: 'Keep Draft'
  },
  th: {
    startHere: 'เริ่มที่นี่',
    createTitle: 'ต้องการสร้างร่างข้อตกลงใหม่ใช่ไหม',
    createCopy: 'ดูตัวอย่างร่างเอกสารด้านล่างก่อน เพื่อให้เห็นรูปแบบเอกสารก่อนเริ่มสร้างข้อตกลงใหม่',
    createButton: 'สร้างข้อตกลงใหม่',
    draftSamples: 'ตัวอย่างร่างเอกสาร',
    draftSamplesCopy: 'ดูตัวอย่างหน้าตาของร่างข้อตกลงแต่ละประเภทก่อนเริ่ม',
    previewDraft: 'ดูตัวอย่างร่าง',
    previewDraftFrameTitle: 'ตัวอย่างร่างเอกสาร',
    searchPlaceholder: 'ค้นหาตามชื่อ คู่สัญญา ประเทศ หรือประเภท',
    loading: 'กำลังโหลดรายการข้อตกลง...',
    empty: 'ไม่พบข้อตกลงที่ตรงกับการค้นหา',
    stats: {
      drafts: 'ฉบับร่าง',
      inReview: 'กำลังรีวิว',
      approved: 'อนุมัติแล้ว'
    },
    stage: {
      draft: 'ฉบับร่าง',
      inReview: 'กำลังรีวิว',
      approved: 'อนุมัติแล้ว'
    },
    columns: {
      agreement: 'ข้อตกลง',
      type: 'ประเภท',
      partner: 'คู่สัญญา',
      status: 'สถานะ',
      actions: 'การดำเนินการ'
    },
    showing: (count) => `แสดงข้อตกลง ${count} รายการ`,
    openItem: (title) => `เปิด ${title || 'ข้อตกลง'}`,
    deleteItem: (title) => `ลบ ${title || 'ร่างข้อตกลง'}`,
    loadError: 'ไม่สามารถโหลดหน้าข้อตกลงได้ในขณะนี้',
    confirmDeleteTitle: 'ลบร่างข้อตกลง?',
    confirmDeleteText: (title) => `ต้องการลบร่าง "${title || 'ข้อตกลงไม่มีชื่อ'}" ใช่หรือไม่`,
    confirmDeleteButton: 'ลบ',
    cancelDeleteButton: 'เก็บร่างไว้'
  }
}

export default {
  name: 'UserAgreement',
  components: { AppSectionHero },
  data() {
    return {
      agreements: [],
      draftTemplates,
      previewDraft: null,
      searchQuery: '',
      activeStatusFilter: 'all',
      isLoading: false,
      loadError: ''
    }
  },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    localizedDraftTemplates() {
      return this.draftTemplates.map((item) => ({
        ...item,
        description: item.descriptions && item.descriptions[this.localeCode] ? item.descriptions[this.localeCode] : ''
      }))
    },
    filteredAgreements() {
      const statusFiltered = this.agreements.filter(item => this.matchesStatusFilter(item))
      if (!this.searchQuery) return statusFiltered
      const query = this.searchQuery.toLowerCase()
      return statusFiltered.filter(item =>
        String(item.title).toLowerCase().includes(query) ||
        String(item.partner).toLowerCase().includes(query) ||
        String(item.country).toLowerCase().includes(query) ||
        String(item.activityType).toLowerCase().includes(query)
      )
    },
    stats() {
      return [
        { key: 'drafts', label: this.text.stats.drafts, value: this.agreements.filter(item => item.derivedStage === 'draft').length },
        { key: 'in-review', label: this.text.stats.inReview, value: this.agreements.filter(item => item.derivedStage !== 'draft' && item.derivedStage !== 'approved').length },
        { key: 'approved', label: this.text.stats.approved, value: this.agreements.filter(item => item.derivedStage === 'approved').length }
      ]
    },
    heroStats() {
      return this.stats.map((item, index) => ({
        key: item.key,
        label: item.label,
        value: item.value,
        icon: index === 0 ? 'cil-pencil' : (index === 1 ? 'cil-clock' : 'cil-check-circle'),
        iconClass: index === 1 ? 'app-section-stat__icon--attention' : (index === 2 ? 'app-section-stat__icon--active' : 'app-section-stat__icon--total'),
        clickable: true,
        active: this.activeStatusFilter === item.key
      }))
    }
  },
  mounted() {
    this.loadCachedAgreements()
    this.loadAgreements()
  },
  methods: {
    loadCachedAgreements() {
      const cached = readPageCache(USER_AGREEMENTS_CACHE_KEY, USER_AGREEMENTS_CACHE_MAX_AGE)
      if (!Array.isArray(cached) || !cached.length) return
      this.agreements = cached
    },
    matchesStatusFilter(item) {
      if (this.activeStatusFilter === 'all') return true
      if (this.activeStatusFilter === 'drafts') return item.derivedStage === 'draft'
      if (this.activeStatusFilter === 'approved') return item.derivedStage === 'approved'
      if (this.activeStatusFilter === 'in-review') return item.derivedStage !== 'draft' && item.derivedStage !== 'approved'
      return true
    },
    selectStatusCard(stat) {
      const nextFilter = String(stat && stat.key ? stat.key : 'all')
      this.activeStatusFilter = this.activeStatusFilter === nextFilter ? 'all' : nextFilter
    },
    statusPillClass(item) {
      return `status-pill--${getAgreementWorkflowStatusTone(item)}`
    },
    displayStageLabel(item) {
      return getAgreementWorkflowStatusLabel(item)
    },
    async loadAgreements() {
      this.isLoading = true
      this.loadError = ''
      try {
        const response = await Service.agreements('list')
        this.agreements = response && response.data && response.data.data ? response.data.data : []
        writePageCache(USER_AGREEMENTS_CACHE_KEY, this.agreements)
      } catch (error) {
        this.loadError = error && error.response && error.response.data && error.response.data.data && error.response.data.data.message
          ? error.response.data.data.message
          : this.text.loadError
        this.agreements = []
      } finally {
        this.isLoading = false
      }
    },
    async deleteDraft(item) {
      const confirmed = typeof window !== 'undefined'
        ? await showConfirmAlert({
            title: this.text.confirmDeleteTitle,
            text: this.text.confirmDeleteText(item.title),
            confirmButtonText: this.text.confirmDeleteButton,
            cancelButtonText: this.text.cancelDeleteButton
          })
        : true

      if (!confirmed) return

      try {
        await Service.agreements('delete-draft', { id: item.id })
        this.agreements = this.agreements.filter(record => String(record.id) !== String(item.id))
      } catch (error) {
        // show error to user + keep item in list
        // (the existing delete handler was failing silently)
        console.error('deleteDraft failed:', { id: item && item.id, error })
        this.loadError = error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : (this.text.loadError || 'Failed to delete draft')
      }
    },

    previewDraftTemplate(draft) {
      if (!draft) return
      this.previewDraft = draft
    },
    closeDraftPreview() {
      this.previewDraft = null
    },
    openAgreement(item) {
      if (item.derivedStage === 'submitted' || item.derivedStage === 'suggestion' || item.derivedStage === 'revision') {
        this.$router.push(`/dashboard/user-agreements/${item.id}/start-review`)
        return
      }
      if (item.derivedStage === 'approved' || item.derivedStage === 'final-review') {
        this.$router.push(`/dashboard/user-agreements/${item.id}/review-summary?mode=view`)
        return
      }
      this.$router.push(`/dashboard/user-agreements/${item.id}/${item.routeType}`)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-agreements");
@include workspace-toolbar-search;
@include workspace-button-system;

.create-shell {
  background:
    radial-gradient(circle at right top, rgba(254, 194, 96, 0.18), transparent 30%),
    linear-gradient(135deg, rgba(140, 21, 21, 0.06) 0%, rgba(255, 255, 255, 0.96) 52%, rgba(255, 248, 237, 0.98) 100%);
  border: 1px solid #eadfce;
  border-radius: 22px;
  box-shadow: 0 16px 34px rgba(44, 52, 71, 0.08);
  margin-bottom: 16px;
}

.create-shell__body {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.create-shell__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.create-shell__title {
  color: #223045;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.create-shell__copy {
  color: #66758a;
  line-height: 1.6;
  margin-top: 6px;
  max-width: 42rem;
}

.create-shell__button {
  background: linear-gradient(135deg, #9e212f 0%, #8a1728 100%);
  border: 0;
  border-radius: 16px;
  color: #fff;
  box-shadow: 0 14px 28px rgba(140, 21, 21, 0.18);
  font-weight: 700;
  min-width: 240px;
  padding: 14px 20px;
  white-space: nowrap;
}

.create-shell__button:hover,
.create-shell__button:focus {
  background: linear-gradient(135deg, #8a1728 0%, #721220 100%);
  color: #fff;
}

.draft-shell {
  border: 1px solid #e7edf4;
  border-radius: 22px;
  box-shadow: 0 16px 34px rgba(44, 52, 71, 0.08);
  margin-bottom: 16px;
}

.draft-shell__header {
  margin-bottom: 18px;
}

.draft-shell__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.draft-shell__title {
  color: #223045;
  font-size: 1.08rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.draft-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.draft-card {
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #e9edf3;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 100%;
  padding: 18px;
}

.draft-card__type {
  color: #8c1515;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.draft-card__title {
  color: #223045;
  font-size: 1rem;
  font-weight: 800;
}

.draft-card__copy {
  color: #66758a;
  flex: 1;
  line-height: 1.6;
}

.draft-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.draft-card__btn {
  min-height: 40px;
}

.draft-preview-modal {
  align-items: center;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 1100;
}

.draft-preview-modal__card {
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  height: min(88vh, 860px);
  max-width: 1100px;
  overflow: hidden;
  width: min(100%, 1100px);
}

.draft-preview-modal__header {
  align-items: center;
  border-bottom: 1px solid #e7edf4;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
}

.draft-preview-modal__eyebrow {
  color: #8c1515;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.draft-preview-modal__title {
  color: #223045;
  font-size: 1.05rem;
  font-weight: 800;
  margin-top: 6px;
}

.draft-preview-modal__close {
  background: linear-gradient(180deg, #ffffff 0%, #edf2f7 100%);
  border: 0;
  border-radius: 12px;
  color: #344767;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  min-height: 38px;
  min-width: 38px;
}

.draft-preview-modal__body {
  background: #f8fafc;
  flex: 1;
  min-height: 0;
  padding: 0;
}

.draft-preview-modal__frame {
  border: 0;
  height: 100%;
  width: 100%;
}

.search-icon { font-size: 0.9rem; }
.table-shell {
  border-radius: 20px;
}
.table-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.table-head__copy,
.empty-state {
  color: #718096;
}
.agreement-table {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  overflow: hidden;
}
.agreement-table__header,
.agreement-table__row {
  align-items: center;
  column-gap: 20px;
  display: grid;
  grid-template-columns: minmax(240px, 1.5fr) minmax(120px, 0.8fr) minmax(180px, 1fr) minmax(130px, 0.8fr) 132px;
  padding: 18px 20px;
}
.agreement-table__header {
  background: #f8fafc;
  border-bottom: 1px solid #e6ebf1;
  color: #52606d;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.agreement-table__row {
  border-bottom: 1px solid #eef1f6;
}
.agreement-table__row:last-child {
  border-bottom: 0;
}
.agreement-table__cell {
  min-width: 0;
}
.agreement-table__cell--actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.agreement-title {
  color: #344767;
  font-weight: 700;
}
.agreement-meta {
  color: #718096;
  margin-top: 4px;
}
.agreement-value {
  color: #344767;
  line-height: 1.5;
}
.status-pill {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 700;
  justify-content: center;
  min-width: 112px;
  padding: 8px 12px;
  text-align: center;
}
.status-pill--draft {
  background: #e2e3e5;
  color: #383d41;
}
.status-pill--pending {
  background: #fff3cd;
  color: #856404;
}
.status-pill--approved {
  background: #dcfce7;
  color: #166534;
}
.status-pill--admin {
  background: #e0f2fe;
  color: #2563eb;
}
.status-pill--rejected,
.status-pill--revision {
  background: #ffe5e5;
  color: #b91c1c;
}
.table-action-btn {
  font-size: 0.95rem;
  min-height: 38px;
  min-width: 38px;
  padding: 0;
}
.table-action-btn--danger {
  box-shadow: 0 12px 24px rgba(180, 35, 24, 0.14);
}
.empty-state { color: #718096; padding: 24px 0; }
.error-state { color: #b42318; padding: 24px 0; }
@media (max-width: 768px) {
  .create-shell__body,
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .agreement-table__header {
    display: none;
  }

  .agreement-table__row {
    align-items: flex-start;
    grid-template-columns: 1fr;
    row-gap: 10px;
  }

  .agreement-table__cell--actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .create-shell__button {
    min-width: 100%;
    width: 100%;
  }

  .draft-card__actions {
    flex-direction: column;
  }

  .draft-preview-modal {
    padding: 12px;
  }

  .draft-preview-modal__card {
    height: 90vh;
  }
}
</style>
